import React, { useContext, useEffect, useReducer, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditCustomerContext, IEditCustomerContext } from '../../../../editCustomerState/editCustomerContext';
import {
	LocalizationContext,
	localizationInitialState,
	TLocalizationSelectedItem,
	TTypeOfModalWindow,
} from './localizationContext';
import { localizationReducer } from './localizationReducer';
import { LocalizationActionsTypes } from './localizationActions';
import { IGetCustomerLocalizationInfo } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { convertUndefinedFields } from 'utils/helpers';
import { getCustomerLocalizationInfo, updateCustomerLocalization } from 'backend/customers';

export const LocalizationState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(localizationReducer, localizationInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { visibleForm } = useContext<IEditCustomerContext>(EditCustomerContext);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [needRequest, setNeedRequest] = useState<boolean>(false);

	const params = new URL(document.location.href).searchParams;
	const customerID = params.get('customerID');

	const needLocalizationDataRequest =
		(customerID && visibleForm === 'localizationForm' && isFirstRender) || state.needRefetchWordings;

	const { response, refetch, loading } = useFetchData<IGetCustomerLocalizationInfo, string>(
		needRequest ? getCustomerLocalizationInfo : null,
		customerID || '',
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		setNeedRequest(!!needLocalizationDataRequest);
	}, [needLocalizationDataRequest]);

	useEffect(() => {
		setNeedRequest(!!state.needRefetchWordings);
	}, [state.needRefetchWordings]);

	useEffect(() => {
		if (state.needRefetchWordings) {
			refetch();
			dispatch({
				type: LocalizationActionsTypes.REFETCH_WORDINGS,
				payload: {
					needRefetchWordings: false,
				},
			});
		}
	}, [state.needRefetchWordings]);

	useEffect(() => {
		refetch();
	}, [customerID, visibleForm]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: LocalizationActionsTypes.SET_ALL_DATA,
				payload: {
					groups: response.data.groups,
				},
			});
			setIsFirstRender(false);
		}
	}, [response]);

	const refetchWordings = (value: boolean) => {
		dispatch({
			type: LocalizationActionsTypes.REFETCH_WORDINGS,
			payload: {
				needRefetchWordings: value,
			},
		});
	};

	const setGroup = (value: Nullable<TLocalizationSelectedItem>) => {
		dispatch({
			type: LocalizationActionsTypes.SET_GROUP,
			payload: {
				selectedGroup: value,
			},
		});
	};

	const setLanguage = (value: Nullable<TLocalizationSelectedItem>) => {
		dispatch({
			type: LocalizationActionsTypes.SET_LANGUAGE,
			payload: {
				selectedLanguage: value,
			},
		});
	};

	const saveTranslation = async ({
		groupId,
		itemKey,
		languageId,
		value,
	}: {
		groupId: Nullable<string>;
		itemKey: Nullable<string>;
		languageId: string;
		value: string;
	}) => {
		if (!customerID) {
			return false;
		}

		const updatedGroups = state.groups.map((group) =>
			group.id === groupId
				? {
						...group,
						items: group.items.map((item) =>
							item.key === itemKey
								? {
										...item,
										translations: item.translations.map((translation) =>
											translation.languageId === languageId
												? { ...translation, value }
												: translation,
										),
								  }
								: item,
						),
				  }
				: group,
		);

		try {
			showLoader();
			await updateCustomerLocalization(convertUndefinedFields({ ownerId: customerID, groups: updatedGroups }));
			refetchWordings(true);
			return true;
		} catch {
			dispatch({
				type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
			return false;
		} finally {
			hideLoader();
		}
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	return (
		<LocalizationContext.Provider
			value={{
				refetchWordings,
				setGroup,
				setLanguage,
				saveTranslation,
				changeTypeOfModalWindow,
				needRefetchWordings: state.needRefetchWordings,
				loading,
				selectedGroup: state.selectedGroup,
				selectedLanguage: state.selectedLanguage,
				groups: state.groups,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</LocalizationContext.Provider>
	);
};
